import { t } from "@/i18n-js/instance";
import { ConnectButton } from "@/react/components/shared/MembersList/CommunityMembers/ActionButtons/ConnectButton";
import { useDetermineConnectionStatus } from "@/react/components/shared/MembersList/CommunityMembers/hooks/useDetermineConnectionStatus";
import { PROFILE_PAGES } from "@/react/helpers/memberHelper";
import { useProfileInfo } from "@/react/hooks/profile/useProfileInfo";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { useProfileDrawer } from "@circle-react/components/Drawers/ProfileDrawer";
import { usePunditUserContext } from "@circle-react/contexts";
import { Icon } from "@circle-react-shared/Icon";
import { Button } from "@circle-react-shared/uikit/Button";
import { ActiveButton } from "../../../ProfileModalV3/Body/ConnectBanner/ActiveButton";
import { MessageButton } from "./MessageButton";

interface ActionButtonsProps {
  closeCard: () => void;
  communityMemberId: number | string;
  messagingEnabled?: boolean;
  publicUid?: string;
}

const params = {
  page_name: PROFILE_PAGES.PROFILE_VIEW,
};

export const ActionButtons = ({
  communityMemberId,
  publicUid,
  messagingEnabled,
  closeCard,
}: ActionButtonsProps) => {
  const drawer = useProfileDrawer();
  const { currentCommunityMember } = usePunditUserContext();

  const isCurrentCommunityMember =
    // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible.
    currentCommunityMember?.id === communityMemberId;

  const shouldShowMessageButton =
    !isCurrentCommunityMember &&
    currentCommunityMember?.messaging_enabled &&
    messagingEnabled;

  const { getProfileInfoQuery } = useProfileInfo({
    publicId: isCurrentCommunityMember ? undefined : publicUid,
    params,
  });

  const isConnectEnabled = useIsConnectEnabled();
  const profile = getProfileInfoQuery.data;
  const isProfileLoading = getProfileInfoQuery.isLoading;
  const connection = profile?.connection_to_current_community_member;
  const { isEmpty, isActive } = useDetermineConnectionStatus(connection);
  const isConnectionEmpty = isCurrentCommunityMember ? false : isEmpty;

  return (
    <div className="mt-4 flex w-full space-x-2">
      {!isCurrentCommunityMember && isProfileLoading && isConnectEnabled && (
        <Button
          variant="secondary"
          disabled
          className="!border-primary !h-8 w-full gap-2 !px-4 !font-medium"
        >
          <Icon type="loader" viewBox="0 0 16 16" />
        </Button>
      )}

      {!isProfileLoading &&
        isConnectionEmpty &&
        !isCurrentCommunityMember &&
        isConnectEnabled &&
        profile && <ConnectButton recipient={profile} />}

      {!isProfileLoading && isActive && (
        <ActiveButton className="!bg-secondary" />
      )}

      {!isProfileLoading && shouldShowMessageButton && (
        <MessageButton
          closeCard={closeCard}
          communityMemberId={communityMemberId}
        />
      )}

      {publicUid && (
        <Button
          className="!border-primary flex items-center justify-center space-x-2 !px-3 !py-1.5 !font-medium"
          full={!shouldShowMessageButton}
          variant="secondary"
          onClick={() => {
            void drawer.show({
              memberPublicId: publicUid,
            });
            closeCard();
          }}
        >
          <Icon type="16-user" useWithFillCurrentColor size={16} aria-hidden />
          <span>{t("view_profile")}</span>
        </Button>
      )}
    </div>
  );
};
